import { Box, Heading, useBreakpointValue } from '@hover/blueprint';
import React, { useState } from 'react';

import { MobileHeader } from 'src/modules/homeownerApp/components/MobileHeader';
import { ThreeDFusionRedirect } from 'src/modules/homeownerApp/components/ThreeDFusion/ThreeDFusionRedirect';
import { useAutoGenerateAIImage } from 'src/modules/homeownerApp/hooks/useAutoGenerateAIImage';
import { useAiOriginalImagesQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useQueryParam,
  useTranslate,
} from 'src/shared/hooks';

import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';
import { useUploadFailure } from '../../hooks/useUploadFailure';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import { mobileNavbarHeight } from '../Navbar';
import { sortAiOriginalImages } from '../Utils';

import { HomeButtonsBar } from './HomeButtonsBar/HomeButtonsBar';
import { MasonryGrid } from './MasonryGrid/MasonryGrid';
import { sampleImages } from './SampleImages/sampleImages';

export type ImageKindType = 'designIdeas' | 'samples';

const LoggedInHome = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const [selectedButton, setSelectedButton] =
    useState<ImageKindType>('designIdeas');

  useUploadFailure();
  const { loggedIn, setCurrentMenu } = useHomeownerAppContext();
  const jobId = useQueryParam('jobId') as string | undefined;

  useAutoGenerateAIImage({ jobId });

  const { data, loading: isLoading } = useAiOriginalImagesQuery({ ssr: false });

  const aiOriginalImages = sortAiOriginalImages(data);

  const hasNoImages =
    aiOriginalImages && aiOriginalImages.length === 0 && !isLoading;

  const title = useBreakpointValue({
    base: t('home.designIdeas'),
    tablet: hasNoImages ? t('home.tryASampleSpace') : t('home.designIdeas'),
  });

  const shouldShowDesignIdeas = useBreakpointValue({
    base: selectedButton === 'designIdeas',
    tablet: true,
  });

  const shouldShowSampleSpaces = useBreakpointValue({
    base: selectedButton === 'samples',
    tablet: !hasNoImages,
  });

  const images = useBreakpointValue({
    base: aiOriginalImages,
    tablet: hasNoImages ? sampleImages : aiOriginalImages,
  });

  const handleUploadClick = () => {
    setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
  };

  return (
    <Box
      flexDirection="column"
      pb={{ base: mobileNavbarHeight, tablet: 0 }}
      pt={{ base: 'env(safe-area-inset-top)', tablet: 800 }}
    >
      <ThreeDFusionRedirect jobId={jobId} />
      <MobileHeader
        display={{ base: 'flex', tablet: 'none' }}
        headingText={title}
        isLoading={false}
        showHeaderText={false}
        showProfileIcon={loggedIn}
        showSubHeading={false}
      />
      <Box
        alignItems={{ base: 'start', tablet: 'center' }}
        flexDir={{ base: 'column', tablet: 'row' }}
        gap="300"
        justifyContent={{ tablet: 'space-between' }}
        mx={{ base: '500', tablet: '800' }}
      >
        <Heading
          color="neutral.900"
          display={{ base: 'none', tablet: 'unset' }}
          flexShrink={0}
          mb="0"
        >
          {title}
        </Heading>

        <HomeButtonsBar
          handleUploadClick={handleUploadClick}
          mt={{ base: 400, tablet: 'unset' }}
          selectedButton={selectedButton}
          setSelectedButton={setSelectedButton}
          width="100%"
        />
      </Box>
      {shouldShowDesignIdeas && (
        <MasonryGrid images={images} isLoading={isLoading} />
      )}
      {shouldShowSampleSpaces && (
        <Box flexDirection="column">
          <Heading
            color="neutral.900"
            display={{ base: 'none', tablet: 'block' }}
            mb="0"
            ml={{ base: '500', tablet: '800' }}
          >
            {t('home.sampleSpaces')}
          </Heading>
          <MasonryGrid
            images={sampleImages}
            isLoading={isLoading}
            isSampleSpaces
            placeholderAspectRatio="3/4"
            placeholderCount={sampleImages.length}
          />
        </Box>
      )}
    </Box>
  );
};

export { LoggedInHome };
